import * as THREE from 'three';
import { GeoBuilder, propMesh, shadeByHeight } from './geo.js';
import { heightAt, baseHeightAt } from './terrain.js';
import { PLAZA_R, lakes, POND, LAKE, WATER_LEVEL } from './layout.js';
import { PuffSystem } from './effects.js';
import { glyphTexture, blobTexture } from './textures.js';
import { TAU, rr, rnd, pick, chance, clamp, lerp, damp, dampAngle, smoothstep, wrapAngle, angleDiff } from './util.js';

const SKIN = ['#ffd6b0', '#f0b98d', '#c98b62', '#8d5a3b', '#ffe0c2'];
const SHIRT = ['#ff6f91', '#4d96ff', '#ffd166', '#6bcb77', '#c77dff', '#ff9a5a', '#f8f4e6', '#2ec4b6'];
const PANTS = ['#3b4a6b', '#5a3b2b', '#2f3e46', '#6b4e71', '#40513b'];
const HAIR = ['#3b2a1a', '#7a4a21', '#e6c15a', '#1f1a24', '#c9552f', '#9aa0a6'];

const tmp = new THREE.Vector3();

// ------------------------------------------------------------------ villagers
class Villager {
  constructor(scene, opts) {
    this.kid = opts.kid;
    const S = this.kid ? 0.72 : 1;
    this.root = new THREE.Group();
    this.root.scale.setScalar(S);
    const skin = new THREE.MeshStandardMaterial({ color: pick(SKIN), roughness: 0.8 });
    const shirt = new THREE.MeshStandardMaterial({ color: pick(SHIRT), roughness: 0.8 });
    const pants = new THREE.MeshStandardMaterial({ color: pick(PANTS), roughness: 0.8 });
    const hairM = new THREE.MeshStandardMaterial({ color: pick(HAIR), roughness: 0.9 });
    const body = new THREE.Group();
    this.body = body;
    this.root.add(body);
    const torso = new THREE.Mesh(new THREE.CapsuleGeometry(0.27, 0.42, 4, 10), shirt);
    torso.position.y = 1.05;
    torso.castShadow = true;
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.26, 14, 10), skin);
    head.position.y = 1.72;
    head.castShadow = true;
    const eyeM = new THREE.MeshBasicMaterial({ color: '#2a2130' });
    for (const s of [-1, 1]) {
      const e = new THREE.Mesh(new THREE.SphereGeometry(0.035, 6, 5), eyeM);
      e.position.set(s * 0.09, 1.75, 0.235);
      body.add(e);
    }
    // hair / hat
    const hat = Math.floor(rnd() * 4);
    if (hat === 0) {
      const h = new THREE.Mesh(new THREE.SphereGeometry(0.28, 12, 8, 0, TAU, 0, Math.PI * 0.55), hairM);
      h.position.y = 1.74;
      h.rotation.x = -0.25;
      body.add(h);
    } else if (hat === 1) {
      const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 0.04, 14), new THREE.MeshStandardMaterial({ color: '#e6c15a', roughness: 0.9 }));
      brim.position.y = 1.9;
      const top = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.24, 0.2, 12), brim.material);
      top.position.y = 2.0;
      body.add(brim, top);
    } else if (hat === 2) {
      const cap = new THREE.Mesh(new THREE.SphereGeometry(0.28, 12, 8, 0, TAU, 0, Math.PI * 0.5), new THREE.MeshStandardMaterial({ color: pick(SHIRT), roughness: 0.8 }));
      cap.position.y = 1.76;
      body.add(cap);
    } else {
      const h = new THREE.Mesh(new THREE.SphereGeometry(0.3, 12, 8, 0, TAU, 0, Math.PI * 0.62), hairM);
      h.position.set(0, 1.72, -0.03);
      body.add(h);
    }
    body.add(torso, head);
    // limbs (pivot at the hip / shoulder)
    this.legs = [];
    this.arms = [];
    for (const s of [-1, 1]) {
      const legPivot = new THREE.Group();
      legPivot.position.set(s * 0.13, 0.72, 0);
      const leg = new THREE.Mesh(new THREE.CapsuleGeometry(0.1, 0.5, 3, 6), pants);
      leg.position.y = -0.32;
      leg.castShadow = true;
      const shoe = new THREE.Mesh(new THREE.SphereGeometry(0.12, 8, 6), new THREE.MeshStandardMaterial({ color: '#33262a' }));
      shoe.scale.set(1, 0.6, 1.4);
      shoe.position.set(0, -0.66, 0.06);
      legPivot.add(leg, shoe);
      body.add(legPivot);
      this.legs.push(legPivot);
      const armPivot = new THREE.Group();
      armPivot.position.set(s * 0.34, 1.32, 0);
      const arm = new THREE.Mesh(new THREE.CapsuleGeometry(0.075, 0.42, 3, 6), shirt);
      arm.position.y = -0.27;
      const hand = new THREE.Mesh(new THREE.SphereGeometry(0.08, 8, 6), skin);
      hand.position.y = -0.54;
      armPivot.add(arm, hand);
      body.add(armPivot);
      this.arms.push(armPivot);
    }
    // ice cream cone for the right hand
    this.cone = new THREE.Group();
    const c = new THREE.Mesh(new THREE.ConeGeometry(0.07, 0.24, 8), new THREE.MeshStandardMaterial({ color: '#e0a458' }));
    c.rotation.x = Math.PI;
    c.position.y = 0.12;
    const sc = new THREE.Mesh(new THREE.SphereGeometry(0.11, 8, 6), new THREE.MeshStandardMaterial({ color: pick(['#ff8fb8', '#7fe6cc', '#fff0b8', '#c9a3ff']), roughness: 0.5 }));
    sc.position.y = 0.3;
    this.cone.add(c, sc);
    this.cone.position.set(0, -0.62, 0.02);
    this.cone.visible = false;
    this.arms[1].add(this.cone);
    scene.add(this.root);

    // emote bubble
    this.bubbleMat = new THREE.SpriteMaterial({ transparent: true, depthWrite: false, fog: false });
    this.bubble = new THREE.Sprite(this.bubbleMat);
    this.bubble.scale.setScalar(0.7);
    this.bubble.position.y = 2.55;
    this.bubble.visible = false;
    this.root.add(this.bubble);

    // state
    this.pos = new THREE.Vector3();
    this.yaw = rnd() * TAU;
    this.speed = 1.4 * (this.kid ? 1.15 : 1) * rr(0.9, 1.15);
    this.target = new THREE.Vector3();
    this.mode = 'idle';
    this.timer = rr(0.5, 4);
    this.walkPh = rnd() * TAU;
    this.hasCone = false;
    this.queueK = -1;
    this.bubbleT = 0;
    this.jump = 0;
    this.vy = 0;
    this.home = null;
  }
}

// ------------------------------------------------------------------ animals
function buildSheep() {
  const b = new GeoBuilder();
  const wool = ['#f7f3ea', '#efe9dc', '#fffdf6'];
  for (let i = 0; i < 6; i++) {
    b.ico(rr(0.34, 0.44), 1, rr(-0.32, 0.32), 0.78 + rr(-0.06, 0.1), rr(-0.42, 0.42), pick(wool), { jitter: 0.1 });
  }
  b.ico(0.5, 1, 0, 0.8, 0, '#f7f3ea', { sy: 0.85, sz: 1.2, jitter: 0.1 });
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) b.cyl(0.05, 0.06, 0.5, 5, sx * 0.2, 0, sz * 0.32, '#3b3438');
  return b.build();
}
function buildSheepHead() {
  const b = new GeoBuilder();
  b.ico(0.19, 1, 0, 0, 0.1, '#3b3438', { sz: 1.25, jitter: 0.06 });
  for (const s of [-1, 1]) b.ico(0.08, 0, s * 0.2, 0.02, 0.0, '#4a4247', { sy: 0.5, sx: 1.5 });
  b.sphere(0.028, -0.07, 0.06, 0.27, '#fff');
  b.sphere(0.028, 0.07, 0.06, 0.27, '#fff');
  return b.build();
}
function buildCow() {
  const b = new GeoBuilder();
  b.ico(0.62, 1, 0, 1.05, 0, '#fbf8f2', { sx: 0.85, sy: 0.85, sz: 1.5, jitter: 0.05 });
  for (let i = 0; i < 5; i++) b.ico(rr(0.2, 0.32), 0, rr(-0.35, 0.35), 1.1 + rr(-0.2, 0.3), rr(-0.7, 0.7), '#2b2529', { sy: 0.6, jitter: 0.05 });
  b.ico(0.16, 0, 0, 0.62, -0.35, '#ffb3c1', { sy: 0.7 });
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) b.cyl(0.08, 0.1, 0.75, 6, sx * 0.3, 0, sz * 0.6, '#fbf8f2');
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) b.cyl(0.085, 0.085, 0.14, 6, sx * 0.3, 0, sz * 0.6, '#3b3438');
  b.cyl(0.03, 0.04, 0.7, 4, 0, 0.75, -0.98, '#fbf8f2', { rx: 0.3 });
  b.ico(0.08, 0, 0, 0.72, -1.25, '#2b2529');
  return b.build();
}
function buildCowHead() {
  const b = new GeoBuilder();
  b.ico(0.27, 1, 0, 0, 0.12, '#fbf8f2', { sz: 1.15, jitter: 0.05 });
  b.ico(0.17, 1, 0, -0.08, 0.34, '#ffb3c1', { sz: 0.9, sy: 0.8 });
  b.sphere(0.03, -0.06, -0.06, 0.5, '#5a2e3a');
  b.sphere(0.03, 0.06, -0.06, 0.5, '#5a2e3a');
  for (const s of [-1, 1]) {
    b.ico(0.1, 0, s * 0.3, 0.14, 0.02, '#2b2529', { sy: 0.5, sx: 1.4 });
    b.cone(0.05, 0.22, 5, s * 0.2, 0.22, 0.0, '#f1e3b5', { rz: -s * 0.7 });
    b.sphere(0.04, s * 0.13, 0.08, 0.3, '#2b2529');
  }
  return b.build();
}
function buildDuck() {
  const b = new GeoBuilder();
  b.ico(0.28, 1, 0, 0.12, 0, '#fff8e0', { sz: 1.35, sy: 0.75, jitter: 0.05 });
  b.ico(0.16, 1, 0, 0.36, 0.22, '#3aa365', { jitter: 0.05 });
  b.cone(0.06, 0.16, 5, 0, 0.34, 0.36, '#ff9a2e', { rx: Math.PI / 2 });
  b.sphere(0.022, -0.07, 0.42, 0.3, '#111');
  b.sphere(0.022, 0.07, 0.42, 0.3, '#111');
  b.ico(0.1, 0, 0, 0.16, -0.36, '#fff8e0', { sz: 1.3, sy: 0.6 });
  return b.build();
}
function buildBird() {
  const b = new GeoBuilder();
  b.ico(0.16, 1, 0, 0, 0, '#f6f3ff', { sz: 1.9, sy: 0.7, jitter: 0.05 });
  b.ico(0.1, 0, 0, 0.04, 0.3, '#f6f3ff');
  b.cone(0.03, 0.12, 4, 0, 0.02, 0.42, '#ff9a2e', { rx: Math.PI / 2 });
  return b.build();
}
function buildWing(side) {
  const g = new THREE.BufferGeometry();
  const v = new Float32Array([0, 0, 0.16, side * 0.8, 0, -0.05, 0, 0, -0.2]);
  g.setAttribute('position', new THREE.BufferAttribute(v, 3));
  g.computeVertexNormals();
  return g;
}
function buildButterfly() {
  const g = new THREE.BufferGeometry();
  const v = new Float32Array([0, 0, 0, 0.16, 0, 0.1, 0.13, 0, -0.1]);
  g.setAttribute('position', new THREE.BufferAttribute(v, 3));
  g.computeVertexNormals();
  return g;
}

// ------------------------------------------------------------------ life
export class Life {
  constructor(scene, village, vegetation, van, weasel, audio, notes) {
    this.scene = scene;
    this.village = village;
    this.van = van;
    this.weasel = weasel;
    this.audio = audio;
    this.time = 0;
    this.group = new THREE.Group();
    scene.add(this.group);
    this.houses = village.info.houses.map((h) => ({ x: h.x, z: h.z, r: Math.hypot(h.w, h.d) * 0.5 + 0.2 }));
    this.houses.push({ x: 0, z: village.info.church.z + 1.5, r: 7.2 });
    this.houses.push({ x: 0, z: 0, r: 3.9 }); // fountain

    this._makeVillagers();
    this._makeAnimals(vegetation);
    this._makeSky();
    this._makeSmoke();
    this._makeFireflies();

    this.stopActive = null;
    this.queue = [];
    this.queueSlots = [null, null, null, null, null];
    this.serveT = 0;
  }

  // ------------------------------------------------------------ villagers
  _makeVillagers() {
    const info = this.village.info;
    this.villagers = [];
    const spots = [];
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * TAU;
      spots.push({ x: Math.cos(a) * 6.2, z: Math.sin(a) * 6.2 });
    }
    this.spots = spots.concat(info.benches || [], info.stallSpots || []);
    const N = 17;
    for (let i = 0; i < N; i++) {
      const v = new Villager(this.group, { kid: i % 5 === 3 });
      const d = pick(info.doors);
      v.home = d;
      v.pos.set(d.x + rr(-1, 1), 0, d.z + rr(-1, 1));
      if (chance(0.5)) { const s = pick(this.spots); v.pos.set(s.x + rr(-1, 1), 0, s.z + rr(-1, 1)); }
      this.villagers.push(v);
    }
  }

  _pickWalk(v) {
    const info = this.village.info;
    const roll = rnd();
    let t;
    if (roll < 0.5) t = pick(this.spots);
    else if (roll < 0.8) t = v.home;
    else t = { x: Math.cos(rnd() * TAU) * rr(2, 8), z: Math.sin(rnd() * TAU) * rr(2, 8) };
    v.target.set(t.x + rr(-1.2, 1.2), 0, t.z + rr(-1.2, 1.2));
    v.mode = 'walk';
  }

  _steer(v, dt, speed, tx, tz) {
    // head toward target with house avoidance
    let dx = tx - v.pos.x, dz = tz - v.pos.z;
    const d = Math.hypot(dx, dz);
    if (d > 0.01) { dx /= d; dz /= d; }
    let ax = 0, az = 0;
    for (const h of this.houses) {
      const ox = v.pos.x - h.x, oz = v.pos.z - h.z;
      const dist2 = ox * ox + oz * oz;
      const R = h.r + 1.2;
      if (dist2 < R * R) {
        const dist = Math.sqrt(dist2) || 0.01;
        const push = (R - dist) / R;
        ax += (ox / dist) * push * 2.4;
        az += (oz / dist) * push * 2.4;
        // slide around: add a tangent component
        ax += (-oz / dist) * push * 1.4;
        az += (ox / dist) * push * 1.4;
      }
    }
    let vx = dx + ax, vz = dz + az;
    const l = Math.hypot(vx, vz) || 1;
    vx /= l;
    vz /= l;
    v.yaw = dampAngle(v.yaw, Math.atan2(vx, vz), 9, dt);
    v.pos.x += vx * speed * dt;
    v.pos.z += vz * speed * dt;
    // hard push-out
    for (const h of this.houses) {
      const ox = v.pos.x - h.x, oz = v.pos.z - h.z;
      const dist = Math.hypot(ox, oz);
      if (dist < h.r + 0.3) { v.pos.x = h.x + (ox / (dist || 1)) * (h.r + 0.3); v.pos.z = h.z + (oz / (dist || 1)) * (h.r + 0.3); }
    }
    return d;
  }

  _say(v, glyph) {
    if (!this._glyphs) {
      this._glyphs = {
        '!': glyphTexture('!', { size: 96, font: '700 84px Fredoka, Arial, sans-serif', color: '#ffe14a', stroke: '#3b2a5c', strokeW: 10 }),
        '♥': glyphTexture('♥', { size: 96, font: '700 84px Arial, sans-serif', color: '#ff6fa3', stroke: '#3b2a5c', strokeW: 10 }),
        '?': glyphTexture('?', { size: 96, font: '700 84px Fredoka, Arial, sans-serif', color: '#8ecbff', stroke: '#3b2a5c', strokeW: 10 }),
      };
    }
    v.bubbleMat.map = this._glyphs[glyph];
    v.bubbleMat.needsUpdate = true;
    v.bubble.visible = true;
    v.bubbleT = 1.6;
  }

  /** Called when the van stops: send nearby villagers to the hatch. */
  onVanStop() {
    this.queue.length = 0;
    const van = this.van;
    let k = 0;
    const cands = this.villagers.filter((v) => v.mode !== 'scared' && !v.hasCone)
      .map((v) => ({ v, d: Math.hypot(v.pos.x - van.pos.x, v.pos.z - van.pos.z) }))
      .filter((o) => o.d < 60)
      .sort((a, b) => a.d - b.d);
    for (const { v } of cands.slice(0, 5)) {
      v.mode = 'run-to-van';
      v.queueK = k++;
      v.speedBoost = 2.4;
      this._say(v, '♥');
    }
  }

  _updateVillagers(dt, time) {
    const van = this.van, weasel = this.weasel;
    const serving = van.state === 'serving';
    for (const v of this.villagers) {
      v.timer -= dt;
      if (v.bubbleT > 0) { v.bubbleT -= dt; v.bubble.visible = v.bubbleT > 0; v.bubble.position.y = 2.55 + Math.sin(time * 8) * 0.06; }

      // scare check
      const wd = Math.hypot(weasel.head.x - v.pos.x, weasel.head.z - v.pos.z);
      if (wd < 3.2 && weasel.speed > 2.5 && v.mode !== 'scared' && v.mode !== 'run-to-van') {
        v.mode = 'scared';
        v.timer = rr(1.4, 2.2);
        v.vy = 4.2;
        this._say(v, '!');
        v.flee = Math.atan2(v.pos.x - weasel.head.x, v.pos.z - weasel.head.z);
      }

      let speed = 0;
      switch (v.mode) {
        case 'idle':
          if (v.timer <= 0) this._pickWalk(v);
          break;
        case 'walk': {
          const d = this._steer(v, dt, v.speed, v.target.x, v.target.z);
          speed = v.speed;
          if (d < 0.5) { v.mode = 'idle'; v.timer = rr(1.5, 6); }
          break;
        }
        case 'scared': {
          const s = v.speed * 2.6;
          v.pos.x += Math.sin(v.flee) * s * dt;
          v.pos.z += Math.cos(v.flee) * s * dt;
          v.yaw = dampAngle(v.yaw, v.flee, 10, dt);
          speed = s;
          this._steer(v, 0, 0, v.pos.x, v.pos.z);
          if (v.timer <= 0) { v.mode = 'idle'; v.timer = rr(0.6, 2); }
          break;
        }
        case 'run-to-van': {
          const spot = van.hatchSpot(v.queueK, tmp);
          if (!serving && van.speed > 0.5) { v.mode = 'walk'; this._pickWalk(v); this._say(v, '?'); break; }
          const d = this._steer(v, dt, v.speed * 2.2, spot.x, spot.z);
          speed = v.speed * 2.2;
          if (d < 0.6) { v.mode = 'queue'; v.timer = rr(2.2, 3.6) + v.queueK * 0.4; }
          break;
        }
        case 'queue': {
          const spot = van.hatchSpot(v.queueK, tmp);
          v.pos.x = damp(v.pos.x, spot.x, 5, dt);
          v.pos.z = damp(v.pos.z, spot.z, 5, dt);
          // face the hatch (van local -X)
          v.yaw = dampAngle(v.yaw, van.yaw - Math.PI / 2, 8, dt);
          if (v.timer <= 0) {
            v.hasCone = true;
            v.mode = 'eat-walk';
            v.timer = rr(9, 14);
            v.vy = 3.4;
            this._say(v, '♥');
            this.audio.sfx('ding');
            this._pickWalk(v);
            v.mode = 'eat-walk';
          }
          if (!serving && van.speed > 0.5) { v.mode = 'walk'; this._pickWalk(v); }
          break;
        }
        case 'eat-walk': {
          const d = this._steer(v, dt, v.speed * 0.7, v.target.x, v.target.z);
          speed = v.speed * 0.7;
          if (d < 0.6) { this._pickWalk(v); v.mode = 'eat-walk'; }
          if (v.timer <= 0) { v.hasCone = false; v.mode = 'walk'; }
          break;
        }
      }
      // if the weasel steals the show, no cone stays forever
      v.cone.visible = v.hasCone;

      // vertical hop physics
      if (v.vy !== 0 || v.jump > 0) {
        v.vy -= 16 * dt;
        v.jump += v.vy * dt;
        if (v.jump <= 0) { v.jump = 0; v.vy = 0; }
      }
      const gy = heightAt(v.pos.x, v.pos.z);
      v.root.position.set(v.pos.x, gy + v.jump, v.pos.z);
      v.root.rotation.y = v.yaw;

      // animation
      v.walkPh += speed * dt * 4.2;
      const sw = Math.sin(v.walkPh) * Math.min(1, speed / 1.4) * 0.7;
      v.legs[0].rotation.x = sw;
      v.legs[1].rotation.x = -sw;
      v.arms[0].rotation.x = -sw * 0.8;
      v.arms[1].rotation.x = v.hasCone ? -1.55 + Math.sin(time * 5 + v.walkPh) * 0.1 : sw * 0.8;
      if (v.mode === 'scared') { v.arms[0].rotation.x = -2.6; v.arms[1].rotation.x = -2.6; }
      if (v.mode === 'queue') v.arms[0].rotation.z = Math.sin(time * 3 + v.queueK) * 0.1;
      v.body.position.y = Math.abs(Math.sin(v.walkPh)) * 0.05 * Math.min(1, speed / 1.4);
      v.body.rotation.z = Math.sin(v.walkPh) * 0.04 * Math.min(1, speed / 1.4);
      const breathe = 1 + Math.sin(time * 2 + v.walkPh) * 0.008;
      v.body.scale.y = breathe;
    }
  }

  // ------------------------------------------------------------ animals
  _makeAnimals(vegetation) {
    const info = this.village.info;
    this.pens = [...(info.pens || []), ...(vegetation.info.pens || [])];
    this.animals = [];
    const sheepGeo = buildSheep(), sheepHead = buildSheepHead(), cowGeo = buildCow(), cowHead = buildCowHead();
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.95 });
    for (const pen of this.pens) {
      const n = pen.kind === 'cow' ? 3 : 6;
      for (let i = 0; i < n; i++) {
        const g = new THREE.Group();
        const body = new THREE.Mesh(pen.kind === 'cow' ? cowGeo : sheepGeo, mat);
        body.castShadow = true;
        const head = new THREE.Mesh(pen.kind === 'cow' ? cowHead : sheepHead, mat);
        head.castShadow = true;
        head.position.set(0, pen.kind === 'cow' ? 1.15 : 0.85, pen.kind === 'cow' ? 0.95 : 0.55);
        g.add(body, head);
        this.group.add(g);
        this.animals.push({
          g, head, body, pen, kind: pen.kind, u: rr(-pen.w / 2 + 1, pen.w / 2 - 1), v: rr(-pen.d / 2 + 1, pen.d / 2 - 1),
          tu: 0, tv: 0, mode: 'graze', timer: rr(1, 5), yaw: rnd() * TAU, ph: rnd() * TAU, speed: pen.kind === 'cow' ? 0.5 : 0.65,
        });
      }
    }
    // ducks
    const duckGeo = buildDuck();
    this.ducks = [];
    for (const [L, n] of [[POND, 3], [LAKE, 5]]) {
      for (let i = 0; i < n; i++) {
        const m = new THREE.Mesh(duckGeo, mat);
        m.castShadow = true;
        this.group.add(m);
        const a = rnd() * TAU, r = rr(0.2, 0.7) * L.r;
        this.ducks.push({ m, L, x: L.x + Math.cos(a) * r, z: L.z + Math.sin(a) * r, tx: 0, tz: 0, yaw: rnd() * TAU, timer: 0, ph: rnd() * TAU });
      }
    }
  }

  _updateAnimals(dt, time) {
    for (const a of this.animals) {
      a.timer -= dt;
      const pen = a.pen;
      let moving = false;
      if (a.mode === 'graze') {
        if (a.timer <= 0) {
          a.mode = 'walk';
          a.tu = rr(-pen.w / 2 + 1, pen.w / 2 - 1);
          a.tv = rr(-pen.d / 2 + 1, pen.d / 2 - 1);
        }
      } else {
        const du = a.tu - a.u, dv = a.tv - a.v;
        const d = Math.hypot(du, dv);
        if (d < 0.3) { a.mode = 'graze'; a.timer = rr(3, 9); }
        else {
          a.u += (du / d) * a.speed * dt;
          a.v += (dv / d) * a.speed * dt;
          // heading in world: pen local (u,v) -> world
          const wx = (du / d) * Math.cos(pen.ry) + (dv / d) * Math.sin(pen.ry);
          const wz = -(du / d) * Math.sin(pen.ry) + (dv / d) * Math.cos(pen.ry);
          a.yaw = dampAngle(a.yaw, Math.atan2(wx, wz), 4, dt);
          moving = true;
        }
      }
      const wx = pen.x + a.u * Math.cos(pen.ry) + a.v * Math.sin(pen.ry);
      const wz = pen.z - a.u * Math.sin(pen.ry) + a.v * Math.cos(pen.ry);
      a.g.position.set(wx, baseHeightAt(wx, wz) + 0.05, wz);
      a.g.rotation.y = a.yaw;
      const bob = moving ? Math.abs(Math.sin(time * 6 + a.ph)) * 0.05 : 0;
      a.g.position.y += bob;
      a.g.rotation.z = moving ? Math.sin(time * 6 + a.ph) * 0.03 : 0;
      const down = a.mode === 'graze' ? 1 : 0;
      a.headDown = damp(a.headDown || 0, down, 4, dt);
      a.head.rotation.x = a.headDown * 0.9 + Math.sin(time * 1.5 + a.ph) * 0.03;
      a.head.position.y = (a.kind === 'cow' ? 1.15 : 0.85) - a.headDown * 0.35;
    }
    for (const d of this.ducks) {
      d.timer -= dt;
      if (d.timer <= 0) {
        const a = rnd() * TAU, r = Math.sqrt(rnd()) * d.L.r * 0.65;
        d.tx = d.L.x + Math.cos(a) * r;
        d.tz = d.L.z + Math.sin(a) * r;
        d.timer = rr(4, 10);
      }
      const dx = d.tx - d.x, dz = d.tz - d.z;
      const dist = Math.hypot(dx, dz);
      if (dist > 0.3) {
        d.x += (dx / dist) * 0.55 * dt;
        d.z += (dz / dist) * 0.55 * dt;
        d.yaw = dampAngle(d.yaw, Math.atan2(dx, dz), 3, dt);
      }
      d.m.position.set(d.x, WATER_LEVEL - 0.04 + Math.sin(time * 1.6 + d.ph) * 0.025, d.z);
      d.m.rotation.y = d.yaw;
      d.m.rotation.z = Math.sin(time * 1.3 + d.ph) * 0.05;
    }
  }

  // ------------------------------------------------------------ birds / butterflies / balloon
  _makeSky() {
    const birdGeo = buildBird();
    const bmat = new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.9 });
    const wmat = new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.9, side: THREE.DoubleSide, flatShading: true });
    const wingL = buildWing(-1), wingR = buildWing(1);
    this.birds = [];
    for (let f = 0; f < 2; f++) {
      const n = f === 0 ? 7 : 5;
      for (let i = 0; i < n; i++) {
        const g = new THREE.Group();
        const body = new THREE.Mesh(birdGeo, bmat);
        const wl = new THREE.Mesh(wingL, wmat), wr = new THREE.Mesh(wingR, wmat);
        wl.position.y = wr.position.y = 0.03;
        g.add(body, wl, wr);
        g.scale.setScalar(1.6);
        this.group.add(g);
        this.birds.push({ g, wl, wr, f, i, r: f === 0 ? 46 : 78, h: f === 0 ? 34 : 52, a: (i / n) * 0.8 + f * 2, spd: f === 0 ? 0.11 : 0.075, ph: rnd() * TAU, off: rr(-3, 3), offY: rr(-2, 2) });
      }
    }
    // butterflies
    const bfGeo = buildButterfly();
    this.butterflies = [];
    const bcols = ['#ff9ec4', '#ffd166', '#8ecbff', '#c9a3ff', '#ffffff', '#ff8a5c'];
    for (let i = 0; i < 16; i++) {
      const g = new THREE.Group();
      const m = new THREE.MeshBasicMaterial({ color: pick(bcols), side: THREE.DoubleSide });
      const l = new THREE.Mesh(bfGeo, m), r = new THREE.Mesh(bfGeo, m);
      l.scale.x = -1;
      g.add(l, r);
      g.scale.setScalar(1.3);
      this.group.add(g);
      const a = rnd() * TAU, rad = rr(8, 58);
      this.butterflies.push({ g, l, r, cx: Math.cos(a) * rad, cz: Math.sin(a) * rad, ph: rnd() * TAU, sp: rr(0.4, 0.9), rad: rr(3, 8) });
    }
    // hot-air balloon
    const bg = new THREE.Group();
    const env = new THREE.SphereGeometry(4.2, 20, 14);
    env.scale(1, 1.18, 1);
    const pos = env.attributes.position;
    const col = new Float32Array(pos.count * 3);
    const stripe = ['#ff6f91', '#fff4e0', '#4d96ff', '#ffd166'];
    const c = new THREE.Color();
    for (let i = 0; i < pos.count; i++) {
      const a = Math.atan2(pos.getX(i), pos.getZ(i));
      const k = Math.floor(((a + Math.PI) / TAU) * 12) % 4;
      c.set(stripe[k]);
      col.set([c.r, c.g, c.b], i * 3);
    }
    env.setAttribute('color', new THREE.BufferAttribute(col, 3));
    const envMesh = new THREE.Mesh(env, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.7 }));
    envMesh.position.y = 8.6;
    envMesh.castShadow = true;
    const basket = new THREE.Mesh(new THREE.BoxGeometry(1.5, 1.1, 1.5), new THREE.MeshStandardMaterial({ color: '#a36a43', roughness: 1 }));
    basket.position.y = 1.2;
    bg.add(envMesh, basket);
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
      const rope = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 4.6, 4), new THREE.MeshStandardMaterial({ color: '#5a3b2b' }));
      rope.position.set(sx * 0.7, 3.85, sz * 0.7);
      rope.rotation.z = -sx * 0.13;
      rope.rotation.x = sz * 0.13;
      bg.add(rope);
    }
    const flame = new THREE.Mesh(new THREE.ConeGeometry(0.25, 0.7, 6), new THREE.MeshBasicMaterial({ color: new THREE.Color(3, 1.6, 0.4) }));
    flame.position.y = 3.6;
    bg.add(flame);
    this.flame = flame;
    this.balloon = bg;
    bg.scale.setScalar(1.35);
    this.group.add(bg);
  }

  _updateSky(dt, time) {
    for (const b of this.birds) {
      const a = b.a + time * b.spd * (b.f ? -1 : 1);
      const x = Math.cos(a) * (b.r + b.off) + 6, z = Math.sin(a) * (b.r + b.off) - 6;
      const y = b.h + b.offY + Math.sin(time * 0.7 + b.ph) * 1.5;
      b.g.position.set(x, y, z);
      b.g.rotation.y = -a + (b.f ? -Math.PI / 2 : Math.PI / 2) + (b.f ? Math.PI : 0) * 0;
      const flap = Math.sin(time * 9 + b.ph);
      b.wl.rotation.z = flap * 0.7;
      b.wr.rotation.z = -flap * 0.7;
      b.g.rotation.z = Math.sin(time * 0.9 + b.ph) * 0.12;
    }
    for (const f of this.butterflies) {
      const t = time * f.sp + f.ph;
      const x = f.cx + Math.sin(t * 1.3) * f.rad + Math.sin(t * 3.1) * 1.2;
      const z = f.cz + Math.cos(t * 0.9) * f.rad + Math.cos(t * 2.7) * 1.2;
      const y = heightAt(x, z) + 1.3 + Math.sin(t * 2.3) * 0.5 + Math.sin(t * 7) * 0.12;
      f.g.position.set(x, y, z);
      f.g.rotation.y = Math.atan2(Math.cos(t * 1.3) * 1.3 * f.rad, -Math.sin(t * 0.9) * 0.9 * f.rad);
      const fl = Math.sin(time * 22 + f.ph) * 0.9;
      f.l.rotation.z = fl;
      f.r.rotation.z = -fl;
      f.g.visible = this.dayAmt > 0.15;
    }
    // balloon
    const a = time * 0.028 + 1.0;
    this.balloon.position.set(Math.cos(a) * 92, 40 + Math.sin(time * 0.3) * 1.5, Math.sin(a) * 92);
    this.balloon.rotation.y = time * 0.05;
    this.flame.scale.set(1, 0.8 + Math.sin(time * 18) * 0.25 + Math.sin(time * 27) * 0.15, 1);
  }

  // ------------------------------------------------------------ smoke + fireflies
  _makeSmoke() {
    this.smoke = new PuffSystem(this.scene, 190, { color: '#f4f0ea', opacity: 0.78, emissive: 0.1 });
    this.chimneys = this.village.info.chimneys.map((c) => ({ ...c, t: rnd() * 2, on: chance(0.65) }));
  }

  _updateSmoke(dt, time) {
    for (const c of this.chimneys) {
      if (!c.on) continue;
      c.t -= dt;
      if (c.t <= 0) {
        c.t = rr(0.7, 1.1);
        this.smoke.emit(c.x + rr(-0.1, 0.1), c.y, c.z + rr(-0.1, 0.1), rr(0.15, 0.45), rr(0.7, 1.0), rr(-0.1, 0.2), rr(4, 5.5), 0.22, rr(0.9, 1.4));
      }
    }
    this.smoke.update(dt, 0.35);
  }

  _makeFireflies() {
    const N = 140;
    const pos = new Float32Array(N * 3);
    const col = new Float32Array(N * 3);
    this.ff = [];
    for (let i = 0; i < N; i++) {
      const a = rnd() * TAU, r = Math.sqrt(rnd()) * 70;
      this.ff.push({ x: Math.cos(a) * r, z: Math.sin(a) * r, h: rr(0.6, 3.2), ph: rnd() * TAU, sp: rr(0.3, 0.8), rad: rr(0.8, 2.5) });
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    this.ffGeo = g;
    this.fireflies = new THREE.Points(g, new THREE.PointsMaterial({
      size: 0.55, map: blobTexture(64), vertexColors: true, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true, fog: true,
    }));
    this.fireflies.frustumCulled = false;
    this.scene.add(this.fireflies);
  }

  _updateFireflies(dt, time, sky) {
    const night = smoothstep(0.35, 0.8, sky.night);
    this.fireflies.visible = night > 0.02;
    if (!this.fireflies.visible) return;
    const p = this.ffGeo.attributes.position, c = this.ffGeo.attributes.color;
    for (let i = 0; i < this.ff.length; i++) {
      const f = this.ff[i];
      const t = time * f.sp + f.ph;
      const x = f.x + Math.sin(t * 1.1) * f.rad, z = f.z + Math.cos(t * 0.8) * f.rad;
      const y = heightAt(x, z) + f.h + Math.sin(t * 1.7) * 0.5;
      p.setXYZ(i, x, y, z);
      const blink = Math.pow(Math.max(0, Math.sin(time * (0.9 + f.sp) + f.ph * 3)), 3);
      const k = night * (0.12 + blink * 0.9);
      c.setXYZ(i, 0.75 * k, 1.0 * k, 0.25 * k);
    }
    p.needsUpdate = true;
    c.needsUpdate = true;
  }

  // ------------------------------------------------------------ main
  update(dt, time, sky) {
    this.dayAmt = sky.dayW;
    this._updateVillagers(dt, time);
    this._updateAnimals(dt, time);
    this._updateSky(dt, time);
    this._updateSmoke(dt, time);
    this._updateFireflies(dt, time, sky);
  }
}
